import { Component, Inject, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { User } from '../../../models/users/user';
import { UserService } from '../../../models/users/userService';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import {
  Textbox,
  TextModes,
} from '../../../../../../../libs/Shared/components/inputs/textbox/textbox';
import { AssignedUser } from '../../../models/users/assignedUser';
import { ContactType } from '../../../core/constants';

export enum ContactDialogMode {
  CheckEmail = 'checkEmail',
  ExistingUser = 'existingUser',
  NewUser = 'newUser',
}

export interface AddMerchantContactDialogData {
  merchantId: string;
  contact?: AssignedUser;
}

@Component({
  selector: 'app-add-merchant-contact-dialog',
  templateUrl: './add-merchant-contact-dialog.component.html',
  standalone: false,
})
export class AddMerchantContactDialogComponent {
  TextModes = TextModes;
  Mode = ContactDialogMode;
  contactTypeOptions = AssignedUser.ALL_CONTACT_TYPES;

  mode: ContactDialogMode = ContactDialogMode.CheckEmail;

  userFormData: Partial<User> = User.createEmpty();
  oldContactTypes: string[] = []

  showRequired: boolean = true;
  isChecking: boolean = false;
  isValid: boolean = false;
  isExistingContact: boolean = false;

  @ViewChildren(Textbox) validatedTextBoxes: QueryList<Textbox> | undefined;

  constructor(
    public dialogRef: MatDialogRef<AddMerchantContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddMerchantContactDialogData,
    private userService: UserService,
    private uiErrorHandler: UIErrorHandler,
  ) {}

  ngOnInit() {
    if (this.data.contact) {
      this.userFormData = User.clone(this.data.contact);
      this.mode = ContactDialogMode.ExistingUser;
      this.isExistingContact = true;
      this.oldContactTypes = this.data.contact ? [...this.data.contact.contactTypes] : [];
    }
  }

  // Email is editable only while we haven't checked it yet; once we know
  // whether the user exists, it becomes read-only either way.
  get isEmailReadonly(): boolean {
    return this.mode !== ContactDialogMode.CheckEmail;
  }

  // The rest of the fields are read-only only when we found an existing user.
  get isFieldReadonly(): boolean {
    return this.mode === ContactDialogMode.ExistingUser;
  }

  get rightButtonLabel(): string {

    if (this.isExistingContact) {
      return 'Update';
    }

    switch (this.mode) {
      case ContactDialogMode.CheckEmail:
        return 'Check';
      case ContactDialogMode.ExistingUser:
        return 'Add Contact';
      case ContactDialogMode.NewUser:
        return 'Create Contact';
    }
  }

  get isRightButtonDisabled(): boolean {
    if (this.isChecking) {
      return true;
    }

    if (this.mode === ContactDialogMode.CheckEmail) {
      return (
        this.isValid
      );
    }

    if (this.mode === ContactDialogMode.NewUser) {
      return !this.isValid;
    }

    // ExistingUser: fields are readonly, nothing further to validate.
    return false;
  }

  checkValidity(): boolean {
    if (this.isExistingContact) {
      const sameContactTypes = JSON.stringify(this.oldContactTypes) === JSON.stringify(this.userFormData.contactTypes)
      this.isValid = !sameContactTypes;
      return this.isValid;
    }

    const textBoxesValid = !this.validatedTextBoxes
      ? false
      : this.validatedTextBoxes
          .toArray()
          .map((control) => control.checkValiditySilently())
          .every((valid) => valid);
  
    this.isValid = textBoxesValid;
    return this.isValid;
  }

  btnRightClick() {
    switch (this.mode) {
      case ContactDialogMode.CheckEmail:
        this.checkIfUserExists();
        break;
      case ContactDialogMode.ExistingUser:
        this.confirm();
        break;
      case ContactDialogMode.NewUser:
        this.create();
        break;
    }
  }

  checkIfUserExists() {
    if (!this.userFormData.emailAddress) {
      return;
    }

    this.isChecking = true;

    this.userService
      .getSuiteUserByEmail(this.userFormData.emailAddress)
      .then((user: User | undefined) => {
        if (user) {
          this.userFormData = User.clone(user);
          this.mode = ContactDialogMode.ExistingUser;
        } else {
          this.mode = ContactDialogMode.NewUser;
          // Give Angular a tick to render the now-visible fields and
          // populate validatedTextBoxes before we compute validity.
          setTimeout(() => this.checkValidity());
        }
      })
      .catch((message: any) => {
        this.uiErrorHandler.handleError(message);
      })
      .finally(() => {
        this.isChecking = false;
      });
  }

  async confirm() {
    // TODO: wire up to actual save flow
    this.isChecking = true;
    try {
      await this.userService.addMerchantUser(this.data.merchantId, this.userFormData, 'portal.merchant')
    } catch(message: any) {
      this.uiErrorHandler.handleError(message);
    } finally {
      this.isChecking = false;
    }
    this.dialogRef.close(true);
  }

  async create() {
    // TODO: wire up to actual create flow
    this.isChecking = true;
    try {
      await this.userService.addMerchantUser(this.data.merchantId, this.userFormData, 'portal.merchant')
    } catch(message: any) {
      this.uiErrorHandler.handleError(message);
    } finally {
      this.isChecking = false;
    }
    this.dialogRef.close(true);
  }

  btnCancelClick() {
    this.dialogRef.close();
  }
}